import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import WhatsAppFAB from "./WhatsAppFAB";
import {
  ArrowLeft, ArrowRight, Lock, Download, Share2, Check, X,
  Users, Building2, Zap, Sliders, RotateCcw, AlertCircle
} from "lucide-react";

const fmtUSD = (n, dec = 0) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  }).format(n);
const fmtNum = (n, dec = 0) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  }).format(n);

// ============== CONSTANTES ==============
const PRECIOS = { empire: 4995, pro: 1497, duo: 997, starter: 697 };
const SEMANAS = 52;

const SETTER_COST = 100;        // por familia (= 50 leads × $2/lead)
const ALQUILER_MES = 700;
const EXTRAS_MES = 1000;
const PROC_PCT = 0.10;
// Comisión dinámica:
// - 75% se entrega SOLO en el plan Travel Empire ($4,995)
// - 50% es la comisión estándar en Starter / Duo / Profesional
const COMISION_OPTIONS = [
  { pct: 50, label: "50% (Starter · Duo · Profesional)", short: "50%", planes: "Starter · Duo · Pro" },
  { pct: 75, label: "75% (Travel Empire)", short: "75%", planes: "Travel Empire" },
];

// === Fórmula 360 — Exit Sale ===
const LEAD_COST = 4;            // $4 por lead
const LEADS_PER_CITA = 25;      // 25 leads para conseguir 1 cita
const VISA_PRICE = 49;          // $49 por asesoría de visa
const FAMILIARES_EXTRA = 2;     // cada comprador adiciona 2 familiares al valor

const DEFAULTS = {
  vendedores: 1,
  citasDia: 2,
  diasSem: 6,   // días/semana (1-7)
  close: 60,    // %
  exitConv: 10, // % de leads no agendados que compran visa (mínimo 10%)
  empire: 40,   // %
  pro: 30,      // %
  duo: 20,      // %
  starter: 10,  // %
};

// Etiqueta del nivel de cierre
const closeLabel = (close) => {
  if (close < 35) return { txt: "Asesor nuevo", color: "#dc2626" };
  if (close < 50) return { txt: "Asesor en formación", color: "#ea580c" };
  if (close < 65) return { txt: "Asesor entrenado", color: "#c9a227" };
  if (close < 75) return { txt: "Asesor experto", color: "#80C753" };
  return { txt: "Top performer", color: "#16a34a" };
};

const Slider = ({ label, value, min, max, step = 1, onChange, suffix = "%", color = "#c9a227", marks = [], note = "" }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="text-xs font-bold text-slate-700">{label}</label>
      <span className="text-xl sm:text-2xl font-black tabular-nums" style={{ color }}>
        {value}{suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-current"
      style={{ color }}
      data-testid={`slider-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    />
    {marks.length > 0 && (
      <div className="flex justify-between text-[10px] text-slate-400 mt-1 px-0.5">
        {marks.map((m) => <span key={m.v}>{m.lbl}</span>)}
      </div>
    )}
    {note && <p className="text-[11px] text-slate-500 mt-1 italic">{note}</p>}
  </div>
);

const AnalisisFinancieroPage = () => {
  const [scrolled, setScrolled] = useState(false);

  // === Sliders state ===
  const [vendedores, setVendedores] = useState(DEFAULTS.vendedores);
  const [citasDia, setCitasDia] = useState(DEFAULTS.citasDia);
  const [diasSem, setDiasSem] = useState(DEFAULTS.diasSem);
  const [close, setClose] = useState(DEFAULTS.close);
  const [exitConv, setExitConv] = useState(DEFAULTS.exitConv);
  const [mEmpire, setMEmpire] = useState(DEFAULTS.empire);
  const [mPro, setMPro] = useState(DEFAULTS.pro);
  const [mDuo, setMDuo] = useState(DEFAULTS.duo);
  const [mStarter, setMStarter] = useState(DEFAULTS.starter);
  const [comisionPct, setComisionPct] = useState(75); // 50 o 75 según plan elegido

  useEffect(() => {
    document.title = "Por qué este negocio funciona — Club Viajemos / Travel Empire";
    const onScroll = () => setScrolled(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: "Análisis Financiero · Club Viajemos", url }); } catch {}
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent("Mira el análisis financiero de la sala de ventas Club Viajemos: " + url)}`, "_blank");
    }
  };

  const reset = () => {
    setVendedores(DEFAULTS.vendedores);
    setCitasDia(DEFAULTS.citasDia);
    setDiasSem(DEFAULTS.diasSem);
    setClose(DEFAULTS.close);
    setExitConv(DEFAULTS.exitConv);
    setMEmpire(DEFAULTS.empire);
    setMPro(DEFAULTS.pro);
    setMDuo(DEFAULTS.duo);
    setMStarter(DEFAULTS.starter);
    setComisionPct(75);
  };

  // === CÁLCULOS REACTIVOS ===
  const calc = useMemo(() => {
    const closeR = close / 100;
    const sumMix = mEmpire + mPro + mDuo + mStarter;
    const safeSum = sumMix > 0 ? sumMix : 100;

    const famMes = (vendedores * citasDia * diasSem * SEMANAS) / 12;

    const ingPond =
      (PRECIOS.empire * mEmpire +
        PRECIOS.pro * mPro +
        PRECIOS.duo * mDuo +
        PRECIOS.starter * mStarter) / safeSum;

    const vpg = ingPond * closeR;
    const compradores = famMes * closeR;
    const brutoMes = famMes * vpg;
    const procMes = brutoMes * PROC_PCT;
    const netoMes = brutoMes - procMes;
    const comisionMes = netoMes * (comisionPct / 100);
    const setterMes = famMes * SETTER_COST;
    const opexMes = ALQUILER_MES + EXTRAS_MES;
    const ebitdaMes = comisionMes - setterMes - opexMes;
    const ebitdaAnual = ebitdaMes * 12;
    const brutoAnual = brutoMes * 12;
    const margenComision = comisionMes > 0 ? ebitdaMes / comisionMes : 0;
    const comisionPorVenta = compradores > 0 ? comisionMes / compradores : 0;

    // === Fórmula 360 — Exit Sale ===
    const leadsTotalMes = famMes * LEADS_PER_CITA;
    const costoMarketingMes = leadsTotalMes * LEAD_COST; // == setterMes
    const leadsNoAgendados = leadsTotalMes - famMes;
    const compradoresVisaMes = leadsNoAgendados * (exitConv / 100);
    const visasVendidasMes = compradoresVisaMes * (1 + FAMILIARES_EXTRA);
    const ingresoVisaMes = visasVendidasMes * VISA_PRICE;
    const ingresoVisaAnual = ingresoVisaMes * 12;
    const gananciaExitSaleMes = ingresoVisaMes - costoMarketingMes;

    return {
      sumMix, ingPond, vpg, compradores, famMes,
      brutoMes, brutoAnual, procMes, netoMes,
      comisionMes, setterMes, opexMes,
      ebitdaMes, ebitdaAnual,
      margenComision, comisionPorVenta,
      closeR,
      // Exit Sale
      leadsTotalMes, costoMarketingMes, leadsNoAgendados,
      compradoresVisaMes, visasVendidasMes,
      ingresoVisaMes, ingresoVisaAnual, gananciaExitSaleMes,
    };
  }, [vendedores, citasDia, diasSem, close, exitConv, mEmpire, mPro, mDuo, mStarter, comisionPct]);

  const lvl = closeLabel(close);
  const sumOk = calc.sumMix === 100;

  return (
    <div data-testid="analisis-financiero-page" className="min-h-screen bg-white">
      {/* Header */}
      <header className={`border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-50 transition-shadow ${scrolled ? "shadow-md" : ""}`}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/analisisfinanciero" data-testid="back-home" className="flex items-center gap-1 text-slate-500 hover:text-[#1D1D1B] text-sm font-medium">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Link>
          <div className="w-px h-5 bg-slate-200" />
          <p className="text-sm font-semibold text-[#1D1D1B] truncate flex-1">Simulador de Ganancias</p>
          <Link
            to="/analisisfinanciero/club-vacacional"
            data-testid="link-negocio-club-vacacional"
            className="hidden md:flex items-center gap-1.5 bg-[#c9a227]/15 hover:bg-[#c9a227]/25 text-[#a17c1a] text-xs font-black px-3 py-1.5 rounded-lg transition-colors border border-[#c9a227]/30"
          >
            Club Vacacional <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to="/analisisfinanciero/certificados"
            data-testid="link-negocio-certificados"
            className="hidden sm:flex items-center gap-1.5 bg-[#80C753]/15 hover:bg-[#80C753]/25 text-[#5a9939] text-xs font-black px-3 py-1.5 rounded-lg transition-colors border border-[#80C753]/30"
          >
            Certificados <ArrowRight className="h-3 w-3" />
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* ============ SLIDE 1: HERO REACTIVO ============ */}
      <section data-testid="slide-hero" className="bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <div className="inline-flex items-center gap-2 bg-[#c9a227]/15 text-[#c9a227] px-3 py-1 rounded-full text-[10px] font-mono tracking-[3px] uppercase mb-5">
            Sala de Ventas · Club Viajemos / Travel Empire
          </div>
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black leading-[0.95] mb-6 max-w-4xl">
            {vendedores === 1 ? "1 persona." : `${vendedores} vendedores.`}<br/>
            {citasDia === 1 ? "1 cita al día" : `${citasDia} citas al día`}{vendedores > 1 ? " c/u." : "."}<br/>
            <span className="text-[#c9a227]" data-testid="hero-anual">{fmtUSD(Math.max(0, calc.ebitdaAnual + calc.gananciaExitSaleMes * 12))}</span><span className="text-[#c9a227]"> al año.</span>
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-5 text-sm sm:text-base">
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Secreto 5</span>
              <span className="font-black text-white tabular-nums">{fmtUSD(Math.max(0, calc.ebitdaAnual))}</span>
            </div>
            <span className="text-slate-500">+</span>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Exit Sale</span>
              <span className="font-black text-[#80C753] tabular-nums">{fmtUSD(Math.max(0, calc.gananciaExitSaleMes * 12))}</span>
            </div>
          </div>
          <p className="text-slate-300 text-lg sm:text-xl max-w-2xl">
            {vendedores === 1 ? "Trabajando solo, " : `Con ${vendedores} vendedores, `}
            {diasSem} día{diasSem > 1 ? "s" : ""} a la semana, con <strong className="text-[#c9a227]">{close}% de cierre</strong> ({lvl.txt.toLowerCase()}).
            Mueve los controles del simulador abajo y todos los números de esta página se actualizan en vivo.
          </p>
        </div>
      </section>

      {/* ============ SLIDE 2: SIMULADOR ============ */}
      <section id="slide-simulador" data-testid="slide-simulador" className="bg-slate-50 border-y-4 border-[#c9a227]">
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
          <div className="flex items-start sm:items-center justify-between gap-3 mb-5 flex-col sm:flex-row">
            <div>
              <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-1 flex items-center gap-1.5">
                <Sliders className="h-3 w-3" /> Prueba tus propios números
              </p>
              <h2 className="text-2xl sm:text-4xl font-black text-[#1D1D1B] leading-tight">
                Simulador interactivo
              </h2>
            </div>
            <button
              type="button"
              onClick={reset}
              data-testid="btn-reset"
              className="flex items-center gap-1.5 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg border border-slate-200 transition-colors"
            >
              <RotateCcw className="h-3 w-3" /> Volver a valores base
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* OPERACIÓN — vendedores + citas */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-[#80C753]/40 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="w-8 h-8 rounded-lg bg-[#80C753]/15 text-[#80C753] flex items-center justify-center text-sm font-black">★</div>
                <h3 className="font-black text-[#1D1D1B]">Operación de tu agencia</h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#80C753]/10 text-[#80C753]">
                  Cada vendedor que sumes multiplica los ingresos
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Slider
                  label="Cantidad de vendedores en tu agencia"
                  value={vendedores}
                  min={1}
                  max={50}
                  onChange={setVendedores}
                  color="#80C753"
                  suffix=""
                  marks={[
                    { v: 1, lbl: "1" },
                    { v: 10, lbl: "10" },
                    { v: 25, lbl: "25" },
                    { v: 50, lbl: "50" },
                  ]}
                  note="Tú + los vendedores que pongas debajo de tu agencia. Como broker puedes llegar a 50."
                />
                <Slider
                  label="Citas por día por vendedor"
                  value={citasDia}
                  min={1}
                  max={5}
                  onChange={setCitasDia}
                  color="#1d4ed8"
                  suffix=""
                  marks={[
                    { v: 1, lbl: "1" },
                    { v: 2, lbl: "2 base" },
                    { v: 3, lbl: "3" },
                    { v: 4, lbl: "4" },
                    { v: 5, lbl: "5" },
                  ]}
                  note="Familias atendidas por cada vendedor cada día."
                />
                <Slider
                  label="Días que trabajas por semana"
                  value={diasSem}
                  min={1}
                  max={7}
                  onChange={setDiasSem}
                  color="#E04A6F"
                  suffix=""
                  marks={[
                    { v: 1, lbl: "1" },
                    { v: 3, lbl: "3" },
                    { v: 5, lbl: "5" },
                    { v: 6, lbl: "6 base" },
                    { v: 7, lbl: "7" },
                  ]}
                  note="De 1 a 7 días. Lunes a sábado (6) es el caso base. Suma domingos para máxima escala."
                />
              </div>

              <div className="mt-4 bg-slate-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 border border-slate-200">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Vendedores</p>
                  <p className="text-xl font-black text-[#80C753] tabular-nums">{vendedores}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Citas/día c/u</p>
                  <p className="text-xl font-black text-[#1d4ed8] tabular-nums">{citasDia}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Días/semana</p>
                  <p className="text-xl font-black text-[#E04A6F] tabular-nums">{diasSem}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Citas/día total</p>
                  <p className="text-xl font-black text-[#1D1D1B] tabular-nums">{vendedores * citasDia}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Familias / mes</p>
                  <p className="text-xl font-black text-[#c9a227] tabular-nums">{fmtNum(calc.famMes, 0)}</p>
                </div>
              </div>
            </div>

            {/* CIERRE */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#c9a227]/15 text-[#c9a227] flex items-center justify-center text-sm font-black">1</div>
                <h3 className="font-black text-[#1D1D1B]">Tasa de cierre</h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full" style={{ backgroundColor: `${lvl.color}15`, color: lvl.color }}>
                  {lvl.txt}
                </span>
              </div>
              <Slider
                label="De cada 100 familias que se sientan, cuántas compran"
                value={close}
                min={25}
                max={80}
                onChange={setClose}
                color={lvl.color}
                marks={[
                  { v: 25, lbl: "25% nuevo" },
                  { v: 40, lbl: "40%" },
                  { v: 60, lbl: "60% entrenado" },
                  { v: 80, lbl: "80% top" },
                ]}
                note="Mínimo 25% (asesor nuevo). 60% es el cierre de un asesor entrenado de Club Viajemos."
              />

              <div className="mt-4 pt-4 border-t border-slate-100">
                <Slider
                  label="Conversión Exit Sale (% de leads no agendados que compran visa)"
                  value={exitConv}
                  min={10}
                  max={40}
                  onChange={setExitConv}
                  color="#c9a227"
                  marks={[
                    { v: 10, lbl: "10% mínimo" },
                    { v: 20, lbl: "20%" },
                    { v: 30, lbl: "30%" },
                    { v: 40, lbl: "40%" },
                  ]}
                  note="Mínimo conservador 10%. Realista 20%. Con campañas afinadas se llega a 30%+."
                />
              </div>
            </div>

            {/* MEZCLA */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="w-8 h-8 rounded-lg bg-[#c9a227]/15 text-[#c9a227] flex items-center justify-center text-sm font-black">2</div>
                <h3 className="font-black text-[#1D1D1B]">Mezcla de planes vendidos</h3>
                <span
                  className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${sumOk ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                >
                  Suma: {calc.sumMix}%
                </span>
              </div>

              <div className="space-y-3.5">
                <Slider label={`Empire ($${PRECIOS.empire})`} value={mEmpire} min={0} max={100} onChange={setMEmpire} color="#c9a227" />
                <Slider label={`Profesional ($${PRECIOS.pro})`} value={mPro} min={0} max={100} onChange={setMPro} color="#1d4ed8" />
                <Slider label={`Duo ($${PRECIOS.duo})`} value={mDuo} min={0} max={100} onChange={setMDuo} color="#80C753" />
                <Slider label={`Starter ($${PRECIOS.starter})`} value={mStarter} min={0} max={100} onChange={setMStarter} color="#E04A6F" />
              </div>

              {!sumOk && (
                <div className="mt-3 flex items-start gap-2 text-amber-700 bg-amber-50 rounded-lg p-2.5 text-xs">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>La mezcla suma {calc.sumMix}%. Idealmente debería sumar 100%. Los cálculos se hacen normalizando al total.</span>
                </div>
              )}

              {/* ─── Toggle % de comisión ─── */}
              <div className="mt-5 pt-5 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                  <div className="w-8 h-8 rounded-lg bg-[#c9a227]/15 text-[#c9a227] flex items-center justify-center text-sm font-black">%</div>
                  <h4 className="font-black text-[#1D1D1B] text-sm">Tu % de comisión</h4>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    actual {comisionPct}%
                  </span>
                </div>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                  El <strong className="text-[#c9a227]">75%</strong> se entrega <strong>solamente</strong> en el plan <strong>Travel Empire</strong>. Los planes Starter, Duo y Profesional pagan hasta <strong>50%</strong>.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {COMISION_OPTIONS.map((opt) => {
                    const active = comisionPct === opt.pct;
                    return (
                      <button
                        key={opt.pct}
                        type="button"
                        onClick={() => setComisionPct(opt.pct)}
                        data-testid={`comision-pct-${opt.pct}`}
                        className={`rounded-xl px-3 py-3 text-left transition-all border-2 ${
                          active
                            ? opt.pct === 75
                              ? 'bg-[#c9a227]/10 border-[#c9a227] shadow-[0_8px_25px_rgba(201,162,39,0.2)]'
                              : 'bg-[#80C753]/10 border-[#80C753] shadow-[0_8px_25px_rgba(128,199,83,0.2)]'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-2xl font-black tabular-nums ${active ? (opt.pct === 75 ? 'text-[#c9a227]' : 'text-[#80C753]') : 'text-slate-400'}`}>
                            {opt.pct}%
                          </span>
                          {active && (
                            <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${opt.pct === 75 ? 'bg-[#c9a227] text-[#1D1D1B]' : 'bg-[#80C753] text-white'}`}>
                              activo
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] font-bold text-slate-700 leading-tight">{opt.planes}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* RESULTADOS LIVE */}
          <div className="mt-5 bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white rounded-2xl p-6 sm:p-8 border border-[#c9a227]/30">
            <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Resultados en vivo</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">VPG</p>
                <p className="text-2xl sm:text-3xl font-black text-[#c9a227] tabular-nums" data-testid="live-vpg">{fmtUSD(Math.max(0, calc.vpg), 2)}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Bruto / mes</p>
                <p className="text-2xl sm:text-3xl font-black text-white tabular-nums" data-testid="live-bruto">{fmtUSD(Math.max(0, calc.brutoMes))}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Utilidad / mes</p>
                <p className="text-2xl sm:text-3xl font-black text-[#80C753] tabular-nums" data-testid="live-utilidad-mes">{fmtUSD(Math.max(0, calc.ebitdaMes))}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Utilidad / año (total)</p>
                <p className="text-2xl sm:text-3xl font-black text-[#c9a227] tabular-nums" data-testid="live-utilidad-anual">{fmtUSD(Math.max(0, calc.ebitdaAnual + calc.gananciaExitSaleMes * 12))}</p>
                <p className="text-[10px] text-slate-500 mt-0.5 tabular-nums">Sec.5 {fmtUSD(Math.max(0, calc.ebitdaAnual))} + ExitSale {fmtUSD(Math.max(0, calc.gananciaExitSaleMes * 12))}</p>
              </div>
            </div>
          </div>

          {/* ====== CALLOUT — SOLO SECRETO #5 ====== */}
          <div data-testid="callout-secreto5" className="mt-5 bg-gradient-to-br from-[#c9a227]/10 via-white to-[#80C753]/10 border-2 border-[#c9a227]/40 rounded-2xl p-5 sm:p-7">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#c9a227] text-[#0a0a14] flex items-center justify-center flex-shrink-0 font-black text-lg shadow-lg">
                5
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-1.5">Importante · Para entender la magnitud</p>
                <h3 className="text-lg sm:text-xl font-black text-[#1D1D1B] mb-2 leading-tight">
                  Estos números se basan <span className="text-[#c9a227]">solo en el Secreto N° 5</span>.
                </h3>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-3">
                  Es decir, <strong className="text-[#1D1D1B]">no incluyen ni un dólar</strong> de los otros productos que como agente Travel Empire ya puedes vender a la misma familia:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
                  {[
                    "Certificados de viaje",
                    "Asesorías de visa B1/B2",
                    "Hoteles",
                    "Tickets aéreos",
                    "Cruceros",
                    "Renta de autos",
                    "Circuitos",
                    "Experiencias",
                    "Seguros de viaje",
                    "Clubes vacacionales",
                  ].map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700"
                    >
                      <X className="h-3 w-3 text-red-500 flex-shrink-0" strokeWidth={3} />
                      <span className="truncate">{p}</span>
                    </span>
                  ))}
                </div>
                <p className="text-sm sm:text-base text-slate-800 leading-relaxed bg-white/70 rounded-xl p-3 border border-[#c9a227]/30">
                  <strong className="text-[#c9a227]">Conclusión:</strong> el cálculo principal de la página solo suma <strong>Secreto 5 + Exit Sale (visas)</strong>.
                  Cualquier otro producto del ecosistema que vendas a las {fmtNum(calc.compradores, 0)} familias del mes —certificados, hoteles, vuelos, cruceros, clubes vacacionales, etc.— se suma encima. Sin sumar gastos fijos ni horas extra.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SLIDE EXIT SALE — FÓRMULA 360 ============ */}
      <section data-testid="slide-exit-sale" className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Fórmula 360 · ningún lead se desperdicia</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-3">
            Tu marketing se paga solo
          </h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-3xl mb-10">
            Para conseguir 1 cita necesitas 25 leads (a $4 cada uno = $100). ¿Qué pasa con los 24 leads que no agendan? Los usamos como base de datos para venderles asesoría de visa. Eso paga el costo del marketing y deja ganancia extra.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <div className="bg-slate-50 rounded-2xl p-5 border-2 border-slate-200">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-slate-500 mb-1">Paso 1</p>
              <p className="text-xs text-slate-600 mb-3">Leads que generas cada mes</p>
              <p className="text-3xl sm:text-4xl font-black text-[#1D1D1B] tabular-nums" data-testid="leads-total">
                {fmtNum(calc.leadsTotalMes, 0)}
              </p>
              <p className="text-xs text-slate-500 mt-2">leads × $4 = <strong className="text-red-600">{fmtUSD(calc.costoMarketingMes)}</strong> en marketing</p>
            </div>

            <div className="bg-[#80C753]/10 rounded-2xl p-5 border-2 border-[#80C753]/30">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#80C753] mb-1">Paso 2 · 1 de 25</p>
              <p className="text-xs text-slate-600 mb-3">Agendan cita presencial</p>
              <p className="text-3xl sm:text-4xl font-black text-[#80C753] tabular-nums">
                {fmtNum(calc.famMes, 0)}
              </p>
              <p className="text-xs text-slate-500 mt-2">familias que se sientan en sala</p>
            </div>

            <div className="bg-[#c9a227]/10 rounded-2xl p-5 border-2 border-[#c9a227]/30">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-1">Paso 3 · 24 de 25</p>
              <p className="text-xs text-slate-600 mb-3">No agendan — base de datos</p>
              <p className="text-3xl sm:text-4xl font-black text-[#c9a227] tabular-nums" data-testid="leads-no-agendados">
                {fmtNum(calc.leadsNoAgendados, 0)}
              </p>
              <p className="text-xs text-slate-500 mt-2">se usan en exit sale (visa)</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white rounded-2xl p-6 sm:p-8 border border-[#c9a227]/30 mb-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 rounded-xl bg-[#c9a227] text-[#0a0a14] flex items-center justify-center font-black text-sm">★</div>
              <h3 className="text-lg sm:text-xl font-black">Exit Sale · Asesoría de visa B1/B2</h3>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Conversión</p>
                <p className="text-2xl font-black text-[#c9a227] tabular-nums">{exitConv}%</p>
                <p className="text-[11px] text-slate-400 mt-0.5">de los no agendados</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Compradores</p>
                <p className="text-2xl font-black text-white tabular-nums" data-testid="compradores-visa">{fmtNum(calc.compradoresVisaMes, 0)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">personas/mes</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Visas vendidas</p>
                <p className="text-2xl font-black text-white tabular-nums">{fmtNum(calc.visasVendidasMes, 0)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">+ 2 familiares c/u</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Precio unitario</p>
                <p className="text-2xl font-black text-[#80C753] tabular-nums">$49</p>
                <p className="text-[11px] text-slate-400 mt-0.5">por visa</p>
              </div>
            </div>

            <div className="border-t border-white/10 pt-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Ingreso visas / mes</p>
                  <p className="text-2xl sm:text-3xl font-black text-[#80C753] tabular-nums" data-testid="ingreso-visa-mes">{fmtUSD(calc.ingresoVisaMes)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Costo marketing / mes</p>
                  <p className="text-2xl sm:text-3xl font-black text-red-400 tabular-nums">−{fmtUSD(calc.costoMarketingMes)}</p>
                </div>
                <div className="bg-[#c9a227]/15 rounded-xl px-4 py-3">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-[#c9a227] mb-1">Ganancia neta exit sale</p>
                  <p className="text-2xl sm:text-3xl font-black text-[#c9a227] tabular-nums" data-testid="ganancia-exit">
                    {calc.gananciaExitSaleMes >= 0 ? "+" : ""}{fmtUSD(calc.gananciaExitSaleMes)}/mes
                  </p>
                  <p className="text-[11px] text-slate-300 mt-0.5">{fmtUSD(calc.gananciaExitSaleMes * 12)}/año adicionales</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#80C753]/15 to-[#c9a227]/15 rounded-2xl p-6 sm:p-7 border-2 border-[#80C753]/40">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#80C753] text-white flex items-center justify-center flex-shrink-0">
                <Check className="h-6 w-6" strokeWidth={3} />
              </div>
              <div className="flex-1">
                <h4 className="text-lg sm:text-xl font-black text-[#1D1D1B] mb-2">
                  El costo de adquisición de cada cita está <span className="text-[#c9a227]">100% subsidiado</span>
                </h4>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                  El exit sale genera <strong className="text-[#80C753]">{fmtUSD(calc.ingresoVisaMes)}/mes</strong> en visas
                  contra <strong className="text-red-600">{fmtUSD(calc.costoMarketingMes)}/mes</strong> de marketing.
                  Tu Secreto 5 funciona con marketing <strong className="text-[#1D1D1B]">gratis</strong> — y además te llevas
                  <strong className="text-[#c9a227]"> {fmtUSD(calc.gananciaExitSaleMes * 12)}/año</strong> de ganancia
                  extra. Por eso le decimos Fórmula 360: ningún lead se desperdicia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SLIDE 3: 4 PASOS ============ */}
      <section data-testid="slide-como-funciona" className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Cómo funciona</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-3">El negocio en 4 pasos</h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mb-10">Lo más simple del mundo. Sin matemática complicada.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { n: "1", t: vendedores === 1 ? `Atiendes ${citasDia} cita${citasDia > 1 ? "s" : ""} al día` : `${vendedores} vendedores · ${citasDia} cita${citasDia > 1 ? "s" : ""}/día c/u`, b: `${diasSem} día${diasSem > 1 ? "s" : ""} a la semana. Eso son ${vendedores * citasDia * diasSem} citas por semana, ${fmtNum(calc.famMes, 0)} familias por mes, ${fmtNum(calc.famMes * 12, 0)} al año.`, c: "#1d4ed8" },
              { n: "2", t: `${Math.round(close)} de cada 100 compran`, b: `De las ${fmtNum(calc.famMes, 0)} familias mensuales, ${fmtNum(calc.compradores, 1)} firman un plan. Es el cierre que tienes cargado.`, c: "#80C753" },
              { n: "3", t: `Cada venta deja ${fmtUSD(calc.comisionPorVenta, 0)} limpio`, b: "Después de la comisión del procesador (10%) y del 25% que va a costos. Es lo que ingresa realmente al bolsillo.", c: "#c9a227" },
              { n: "4", t: vendedores === 1 ? "100% de la utilidad es tuya" : "Sumas vendedores sin aumentar gastos fijos", b: vendedores === 1 ? "Sin sueldos. Sin socios. Sin franquicia. Eres el único dueño y el único vendedor." : "Cada vendedor extra que agregas bajo tu agencia multiplica los ingresos. El alquiler y los extras quedan iguales: solo crecen los setters por la mayor cantidad de familias.", c: "#E04A6F" },
            ].map((s) => (
              <div key={s.n} className="bg-slate-50 rounded-2xl p-6 border-2" style={{ borderColor: `${s.c}30` }}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-xl flex-shrink-0" style={{ backgroundColor: s.c }}>
                    {s.n}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg sm:text-xl font-black text-[#1D1D1B] mb-1">{s.t}</h3>
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{s.b}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SLIDE 4: VPG ============ */}
      <section data-testid="slide-vpg" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 text-center">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">El número que importa</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-3">Value Per Guest</h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto mb-10">
            La métrica que usa toda la industria de timeshare para medir si una sala está sana.
          </p>

          <div className="inline-block bg-gradient-to-br from-[#c9a227] to-[#e0b835] rounded-3xl p-8 sm:p-12 shadow-2xl">
            <p className="text-[10px] sm:text-xs font-mono tracking-[3px] uppercase text-[#1D1D1B]/70 mb-3">VPG actual</p>
            <p className="text-6xl sm:text-8xl font-black text-[#0a0a14] leading-none tabular-nums" data-testid="vpg-display">
              {fmtUSD(Math.max(0, calc.vpg), 0)}
            </p>
            <p className="text-sm sm:text-base text-[#0a0a14]/80 mt-3 font-bold">por cada familia que se sienta</p>
          </div>

          <p className="text-base sm:text-lg text-slate-600 mt-8 max-w-2xl mx-auto leading-relaxed">
            Con tu configuración actual ({close}% de cierre), <strong className="text-[#1D1D1B]">cada familia que entra deja {fmtUSD(calc.vpg, 0)} de ingreso bruto</strong>.
            Un VPG saludable en la industria está entre $400 y $800. {calc.vpg > 800 && <span className="text-[#c9a227] font-bold">El tuyo está por encima del promedio del mercado.</span>}
          </p>
        </div>
      </section>

      {/* ============ SLIDE 5: PLANES (mix dinámico) ============ */}
      <section data-testid="slide-planes" className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Lo que vendes</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-3">4 planes · 1 producto</h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mb-10">
            Tu mezcla actual de cierre — modificá los porcentajes desde el simulador de arriba.
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { plan: "Empire", precio: PRECIOS.empire, mix: mEmpire, color: "#c9a227" },
              { plan: "Profesional", precio: PRECIOS.pro, mix: mPro, color: "#1d4ed8" },
              { plan: "Duo", precio: PRECIOS.duo, mix: mDuo, color: "#80C753" },
              { plan: "Starter", precio: PRECIOS.starter, mix: mStarter, color: "#E04A6F" },
            ].map((p) => {
              const isHighest = p.mix === Math.max(mEmpire, mPro, mDuo, mStarter) && p.mix > 0;
              return (
                <div key={p.plan} className={`rounded-2xl p-6 border-2 bg-slate-50 ${isHighest ? "shadow-xl scale-[1.03]" : ""}`} style={{ borderColor: isHighest ? p.color : `${p.color}30` }}>
                  {isHighest && (
                    <div className="inline-block bg-[#c9a227] text-[#0a0a14] text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded mb-2">
                      Más vendido
                    </div>
                  )}
                  <p className="text-[10px] font-mono tracking-[3px] uppercase mb-1" style={{ color: p.color }}>{p.plan}</p>
                  <p className="text-3xl sm:text-4xl font-black text-[#1D1D1B] leading-none">{fmtUSD(p.precio)}</p>
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-xs text-slate-500">Mezcla de cierre</p>
                    <p className="text-lg font-black tabular-nums" style={{ color: p.color }}>{p.mix}%</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 bg-slate-50 rounded-2xl p-5 border border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-slate-500">Ingreso promedio por venta</p>
              <p className="text-lg sm:text-xl font-bold text-[#1D1D1B]">Después de cualquier cierre</p>
            </div>
            <p className="text-3xl sm:text-4xl font-black text-[#c9a227] tabular-nums" data-testid="ingreso-pond">{fmtUSD(calc.ingPond, 0)}</p>
          </div>
        </div>
      </section>

      {/* ============ SLIDE 6: 3 GASTOS ============ */}
      <section data-testid="slide-gastos" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Estructura de costos</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-3">
            Los <span className="text-[#c9a227]">únicos 3 gastos</span> al mes
          </h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mb-10">
            No hay sueldos. No hay vendedores externos. No hay comisiones cruzadas.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {[
              { icon: Users, k: "Setters (variable)", v: fmtUSD(calc.setterMes) + "/mes", desc: `$100 por cada familia que se sienta. ${fmtNum(calc.famMes, 0)} familias × $100.` },
              { icon: Building2, k: "Alquiler de oficina", v: fmtUSD(ALQUILER_MES) + "/mes", desc: "Costo fijo. Lo único que pagas aunque no vendas." },
              { icon: Zap, k: "Gastos extras", v: fmtUSD(EXTRAS_MES) + "/mes", desc: "Servicios, materiales, imprevistos." },
            ].map((g, i) => {
              const Icon = g.icon;
              return (
                <div key={i} className="bg-white rounded-2xl p-5 border border-slate-200">
                  <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-[10px] font-mono tracking-[2px] uppercase text-slate-500 mb-1">{g.k}</p>
                  <p className="text-xl font-black text-[#1D1D1B]">{g.v}</p>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">{g.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="bg-gradient-to-r from-[#c9a227]/15 to-[#80C753]/10 rounded-2xl p-6 sm:p-8 border-2 border-[#c9a227]/30">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-1">Total gasto al mes</p>
                <p className="text-3xl sm:text-4xl font-black text-[#1D1D1B]">{fmtUSD(calc.setterMes + calc.opexMes)}</p>
                <p className="text-sm text-slate-600 mt-1">Setters {fmtUSD(calc.setterMes)} + fijos {fmtUSD(calc.opexMes)}.</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-mono tracking-[3px] uppercase text-slate-500 mb-1">Comparado con</p>
                <p className="text-2xl sm:text-3xl font-black text-[#80C753] tabular-nums" data-testid="comision-mes">{fmtUSD(Math.max(0, calc.comisionMes))}</p>
                <p className="text-sm text-slate-600 mt-1">de comisión que entra a la sala cada mes.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SLIDE 7: INGRESO ============ */}
      <section data-testid="slide-ingreso" className="bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Lo que se lleva el dueño</p>
          <h2 className="text-3xl sm:text-5xl font-black leading-tight mb-3">
            Sin sueldo. Sin comisiones.<br/>
            <span className="text-[#c9a227]">Solo utilidad pura.</span>
          </h2>
          <p className="text-slate-300 text-base sm:text-lg max-w-2xl mb-10">
            Eres el único dueño. Toda la utilidad neta mensual va para ti. Punto.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white/5 border-2 border-[#c9a227]/40 rounded-2xl p-8 backdrop-blur">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-2">Por mes (total)</p>
              <p className="text-5xl sm:text-6xl font-black text-white leading-none tabular-nums" data-testid="ingreso-mes">{fmtUSD(Math.max(0, calc.ebitdaMes + calc.gananciaExitSaleMes))}</p>
              <div className="mt-3 pt-3 border-t border-white/10 space-y-1 text-[11px] sm:text-xs">
                <div className="flex justify-between text-slate-400">
                  <span>Secreto 5</span>
                  <span className="font-bold text-white tabular-nums">{fmtUSD(Math.max(0, calc.ebitdaMes))}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Exit Sale (visas)</span>
                  <span className="font-bold text-[#80C753] tabular-nums">{fmtUSD(Math.max(0, calc.gananciaExitSaleMes))}</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#c9a227] to-[#e0b835] rounded-2xl p-8 text-[#0a0a14]">
              <p className="text-[10px] font-mono tracking-[3px] uppercase opacity-70 mb-2">Por año (total)</p>
              <p className="text-5xl sm:text-6xl font-black leading-none tabular-nums" data-testid="ingreso-anual">{fmtUSD(Math.max(0, calc.ebitdaAnual + calc.gananciaExitSaleMes * 12))}</p>
              <div className="mt-3 pt-3 border-t border-[#0a0a14]/15 space-y-1 text-[11px] sm:text-xs">
                <div className="flex justify-between opacity-80">
                  <span className="font-bold">Secreto 5</span>
                  <span className="font-black tabular-nums">{fmtUSD(Math.max(0, calc.ebitdaAnual))}</span>
                </div>
                <div className="flex justify-between opacity-80">
                  <span className="font-bold">Exit Sale (visas)</span>
                  <span className="font-black tabular-nums">{fmtUSD(Math.max(0, calc.gananciaExitSaleMes * 12))}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur">
            <p className="text-sm text-slate-300 leading-relaxed">
              <strong className="text-white">Margen sobre la comisión:</strong> {(calc.margenComision * 100).toFixed(1)}%.
              De cada $100 que entra como comisión, te quedan ${(calc.margenComision * 100).toFixed(0)} limpios.
              El día que sumes un segundo vendedor o subas a 3 citas diarias, el ingreso se duplica o triplica sin aumentar gastos fijos.
            </p>
          </div>
        </div>
      </section>

      {/* ============ SLIDE 8: 6 RAZONES ============ */}
      <section data-testid="slide-por-que-funciona" className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Por qué este negocio es espectacular</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-10">
            6 razones que <br className="hidden sm:block"/>cambian el juego
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { t: "Margen altísimo sobre comisión", d: "De cada $100 que entra, una porción gigante queda como utilidad. Casi cualquier negocio del mundo trabaja con 5–15% de margen — este modelo está muy por encima." },
              { t: "Sin sueldos fijos que pagar", d: "No tienes que pagar nómina aunque no vendas. Tu único gasto fijo son $1,700/mes (alquiler + extras)." },
              { t: "Capital inicial mínimo", d: "Con $5,000–$10,000 de capital de trabajo arrancas. La sala paga sus propios costos desde el primer mes con cierres normales." },
              { t: "Modelo probado", d: "Es el mismo modelo que usan los grandes resorts y desarrolladoras de timeshare. Solo que ahora tú eres el dueño." },
              { t: "Escalable sin cambiar la estructura", d: "Subir de 2 a 3 citas/día casi duplica tus ingresos. Sumar un segundo vendedor = duplicar todo. Crece linealmente sin sumar gastos fijos." },
              { t: "100% del control", d: "Sin franquicia, sin royalties, sin comisiones a un mayorista. Eres el dueño total de tu sala." },
            ].map((r, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="w-10 h-10 rounded-xl bg-[#80C753]/15 text-[#80C753] flex items-center justify-center flex-shrink-0">
                  <Check className="h-5 w-5" strokeWidth={3} />
                </div>
                <div className="flex-1">
                  <h3 className="font-black text-lg text-[#1D1D1B] mb-1">{r.t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{r.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SLIDE 9: COMPARACIÓN ============ */}
      <section data-testid="slide-comparacion" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Comparación honesta</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-10">
            Modelo tradicional vs. <br className="hidden sm:block"/>Sala <span className="text-[#c9a227]">propia</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-6 border border-slate-300">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-slate-500 mb-3">Sala tradicional</p>
              <ul className="space-y-3 text-sm">
                {["Vendedores asalariados ($3,000–$5,000/mes cada uno)","Comisiones por venta (10–25% del ticket)","Gerente de sala con sueldo fijo","Royalty al mayorista (15–30% adicional)","Margen neto típico: 8–15%"].map((x, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-slate-700">{x}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-[#1D1D1B] to-[#252548] text-white rounded-2xl p-6 border-2 border-[#c9a227] shadow-xl">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Sala propia (este modelo)</p>
              <ul className="space-y-3 text-sm">
                {["Cero sueldos. Eres el dueño y el vendedor.","Cero comisiones cruzadas. Toda la utilidad es tuya.","Sin gerente externo. Tú te autogestionas.","Sin royalty. La sala es 100% tuya.",`Margen real con tu cierre actual: ${(calc.margenComision * 100).toFixed(1)}% sobre comisión.`].map((x, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[#c9a227] flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section data-testid="slide-cta" className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 text-center">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Tu siguiente paso</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-4">¿Quieres operar este modelo?</h2>
          <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            El Manual completo te enseña cómo armar tu sala, captar las primeras 50 familias y operar con cierres del 60%+.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <Link
              to="/manual27"
              data-testid="cta-financiero-manual27"
              className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#c9a227] to-[#e0b835] hover:from-[#e0b835] hover:to-[#c9a227] text-[#0a0a14] font-black text-base px-8 py-4 rounded-xl uppercase tracking-wider transition-all hover:scale-[1.03] shadow-lg shadow-[#c9a227]/30"
            >
              <Lock className="h-4 w-4" /> Acceder por $27 USD
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/protocolo-2026"
              data-testid="cta-financiero-protocolo"
              className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-[#1D1D1B] font-bold text-sm px-6 py-4 rounded-xl uppercase tracking-wider transition-colors"
            >
              Ver Protocolo 2026
            </Link>
          </div>

          <p className="text-xs text-slate-400">
            <span className="line-through">$297</span> · Hoy $27 USD · Acceso inmediato
          </p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 py-8 text-xs text-slate-400 leading-relaxed">
          <p data-testid="disclaimer-travel-empire" className="text-slate-500 mb-3 max-w-4xl mx-auto">
            <span className="text-slate-600 font-semibold">*</span> Este simulador está calculado al <span className="text-slate-600 font-semibold">{comisionPct}% de comisión</span> ({comisionPct === 75 ? "plan Travel Empire" : "planes Starter / Duo / Profesional"}) y bajo el supuesto de que el agente realiza la venta de principio a fin por cuenta propia. Las comisiones efectivas pueden variar cuando intervienen otras personas en el proceso (setter, closer, supervisor, broker). Para una proyección exacta de tu caso, revisa siempre el plan de negocios y el plan de compensación vigente. Cifras proyectadas; los resultados reales pueden variar.
          </p>
          <p className="text-center pt-3 border-t border-slate-200">
            Tons of Leads LLC · Florida Seller of Travel ST39568 · DBA Viajemos · Modelo financiero — Edición 2026.
          </p>
        </div>
      </footer>
      <WhatsAppFAB context="sala-ventas" />
    </div>
  );
};

export default AnalisisFinancieroPage;
